// =====================================================================
// i18n bootstrap — bundled JSON resources, no async loader (small
// enough to ship with the SPA). Module-level init. Language detection
// happens in `pickInitialLocale()`; the actual switch is done by
// `useLocaleStore` which calls `i18n.changeLanguage()` on mount and
// on every `setLocale()`.
// =====================================================================

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ru from "./locales/ru.json";
import en from "./locales/en.json";
import uz from "./locales/uz.json";

export type Locale = "ru" | "en" | "uz";
export const SUPPORTED_LOCALES: ReadonlyArray<Locale> = ["ru", "en", "uz"];

const resources = {
  ru: { translation: ru },
  en: { translation: en },
  uz: { translation: uz },
} as const;

void i18n.use(initReactI18next).init({
  resources,
  lng: "ru",
  fallbackLng: "ru",
  supportedLngs: [...SUPPORTED_LOCALES],
  // Locales use single-brace placeholders ({count}, {n}); match them
  // (i18next defaults to {{...}}, which would render our keys literally).
  interpolation: { escapeValue: false, prefix: "{", suffix: "}" },
  returnNull: false,
});

const STORAGE_KEY = "eurostyle_locale_v1";

function isLocale(v: string): v is Locale {
  return v === "ru" || v === "en" || v === "uz";
}

function mapTgCode(code: string): Locale | null {
  const c = code.toLowerCase();
  if (c.startsWith("ru")) return "ru";
  if (c.startsWith("en")) return "en";
  if (c.startsWith("uz")) return "uz";
  return null;
}

export function pickInitialLocale(): Locale {
  // 1. Persisted user choice
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isLocale(stored)) return stored;
  } catch {
    /* storage blocked */
  }
  // 2. Telegram user language code
  const tgCode = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code ?? "";
  const fromTg = mapTgCode(tgCode);
  if (fromTg) return fromTg;
  // 3. Browser language
  if (typeof navigator !== "undefined" && navigator.language) {
    const fromNav = mapTgCode(navigator.language);
    if (fromNav) return fromNav;
  }
  return "ru";
}

export { i18n };
